import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { mangaList, categories } from '@/mocks/mangaData';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const category = categories.find((c) => c.slug === slug);
  const categoryManga = mangaList.filter((m) => m.category === slug);

  const allTags = Array.from(new Set(categoryManga.flatMap((m) => m.tags)));

  let filteredManga = selectedTag
    ? categoryManga.filter((m) => m.tags.includes(selectedTag))
    : categoryManga;

  filteredManga = [...filteredManga].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'views') return b.views - a.views;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  useEffect(() => {
    setSelectedTag(null);
    setSortBy('latest');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!category) {
    return <div className="pt-20 text-center">分类未找到</div>;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `女神漫画 - ${category.label} | 免费在线阅读`,
    description: category.description,
    url: `https://goddessmanga.com/category/${slug}`,
  };

  return (
    <>
      <SEOHead
        title={`${category.label}漫画 - 女神漫画免费全集推荐 | 下拉式在线漫画阅读`}
        description={`女神漫画${category.label}频道：${category.description}。提供排行榜精选、下拉式免费在线阅读、追更收藏，尽享${category.label}漫画大全。`}
        keywords={`${category.label},${category.label}推荐,女神漫画,在线漫画,免费全集,下拉式阅读,排行榜`}
        canonicalUrl={`https://goddessmanga.com/category/${slug}`}
        schema={schema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        {/* Category Header */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category.label },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-4 mb-3">
              {category.label}漫画
            </h1>
            <p className="text-foreground-600 max-w-2xl">{category.description}</p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-3 text-sm font-medium cursor-pointer whitespace-nowrap border-b-2 transition-colors ${cat.slug === slug ? 'border-primary-500 text-primary-500' : 'border-transparent text-foreground-600 hover:text-foreground-800'}`}
                >
                  {cat.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Tag Filter */}
        <section className="py-6 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setSelectedTag(null)}
                className={`px-3 py-1.5 text-xs rounded-full cursor-pointer whitespace-nowrap transition-colors ${!selectedTag ? 'bg-primary-500 text-background-50 dark:text-foreground-950' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
              >
                全部
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`px-3 py-1.5 text-xs rounded-full cursor-pointer whitespace-nowrap transition-colors ${selectedTag === tag ? 'bg-primary-500 text-background-50 dark:text-foreground-950' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Sort & Results */}
        <section className="py-8 md:py-12 bg-background-100 min-h-[400px]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <p className="text-sm text-foreground-600">
                共 <strong>{filteredManga.length}</strong> 部漫画
              </p>
              <div className="flex items-center gap-2">
                {(['latest', 'rating', 'views'] as const).map((sort) => (
                  <button
                    key={sort}
                    onClick={() => setSortBy(sort)}
                    className={`px-3 py-1.5 text-xs rounded-full cursor-pointer whitespace-nowrap transition-colors ${sortBy === sort ? 'bg-primary-500 text-background-50 dark:text-foreground-950' : 'bg-background-50 text-foreground-600 hover:text-foreground-800'}`}
                  >
                    {sort === 'latest' ? '最新' : sort === 'rating' ? '评分' : '人气'}
                  </button>
                ))}
              </div>
            </div>

            {filteredManga.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {filteredManga.map((manga) => (
                  <MangaCard key={manga.id} manga={manga} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <i className="ri-emotion-sad-line text-foreground-300 text-3xl"></i>
                </div>
                <p className="text-foreground-500">暂无符合条件的漫画</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}